/**
 * Terminal Apollo transport link that splits GraphQL traffic by operation
 * type so read-heavy initial renders cost fewer HTTP round-trips.
 *
 * Queries are routed to a {@link BatchHttpLink} which coalesces operations
 * dispatched within a short window into a single batched HTTP request (the
 * backend must have `allowBatchedHttpRequests` enabled — SE-5038/SE-5055).
 * Mutations and subscriptions are routed to a plain unbatched {@link HttpLink}:
 * mutations must not be silently coalesced/reordered with reads, and
 * subscriptions are long-lived streams that cannot be batched at all.
 *
 * Extracted from `providers/ApolloClient.tsx` as its own module so the split
 * predicate and batch key are unit-testable in isolation and so the provider
 * file stays within its max-lines budget.
 * @module providers/batchTerminalLink
 */
import { ApolloLink, HttpLink, Operation, split } from "@apollo/client/core";
import type { HttpOptions } from "@apollo/client/core";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { getMainDefinition } from "@apollo/client/utilities";

import { POV_TEAM_ID_HEADER } from "@/providers/povTeamHeaderState";
import {
  BATCH_REQUEST_TIMEOUT_MS,
  createTimeoutFetch,
  REQUEST_TIMEOUT_MS,
} from "@/providers/timeoutFetch";

/** BatchHttpLink: cap on operations coalesced into one HTTP request. */
const BATCH_MAX = 10;

/** BatchHttpLink: window (ms) over which operations are collected to batch. */
const BATCH_INTERVAL_MS = 20;

/**
 * Queries that never share a batched POST (SE-8579).
 *
 * A batch is one Lambda invocation that answers when its SLOWEST member does,
 * so a heavy query holds every light sibling behind it. Measured on staging
 * over 7 days (2026-09-26): `ScoutingListAllTargets` ran p50 122 ms alone and
 * 995 ms in batches of 7+, and a light `ListScenarios` sharing its batch ran
 * p50 232 ms against 89 ms without it. `ScoutingGetWatchlistPitch` is the only
 * related SQL that spills to disk (mean 337 ms), and
 * `SquadPlanningGetSquadPlayersByPosition` ran p50 1,123 ms beside SLA.
 * `PlayerDetailListPlayersByTeamIdsAndPositionIds` (SE-8821, measured under
 * SE-8819 on 2026-10-03) takes ~217 ms of its own server time but p50 ~735 ms
 * in the player-profile batch, in flight for 88% of a ~919 ms batched POST.
 * Its callers pass no `first`, so it always counts as heavy.
 * `PlayerSearchListAvailableCustomStats` (SE-8832, measured under SE-8829 on
 * staging 2026-10-03) can pass the 20 s statement timeout on a cold replica;
 * the cold player-profile batches it rode in took 24.4 s and 17.5 s while every
 * other member answered in under 700 ms. It takes no variables, so it always
 * counts as heavy.
 *
 * Add a name here only from a measurement. Each must exist in a `.graphql`
 * document; the unit test enforces that, because a renamed operation would
 * otherwise drop silently back into the batch.
 */
export const HEAVY_OPERATION_NAMES = [
  "ScoutingListAllTargets",
  "ScoutingGetWatchlistPitch",
  "SquadPlanningGetSquadPlayersByPosition",
  "PlayerDetailListPlayersByTeamIdsAndPositionIds",
  "PlayerSearchListAvailableCustomStats",
] as const;

/** Lookup form of {@link HEAVY_OPERATION_NAMES}. */
const HEAVY_OPERATIONS: ReadonlySet<string> = new Set(HEAVY_OPERATION_NAMES);

/**
 * Smallest `first` at which a {@link HEAVY_OPERATION_NAMES} read counts as
 * heavy (SE-8579).
 *
 * The name alone over-selects: the weak-slot counts ask
 * `ScoutingListAllTargets` for `first: 1` just to read `totalCount`, up to
 * three per Pitch view, and those are cheap enough to keep batching. Every
 * list page the app reads is at least 50 rows (the scouting table and slot
 * pop-out 50, the pitch groups 100, squad planning and export 200), so any
 * threshold between 2 and 50 separates the two. 10 leaves room either side.
 * A read with no `first` gets the schema default (50), so it counts as heavy.
 */
export const HEAVY_MIN_PAGE_SIZE = 10;

/**
 * Whether an operation is a heavy page: a {@link HEAVY_OPERATION_NAMES} read
 * asking for {@link HEAVY_MIN_PAGE_SIZE} rows or more, or for its default page.
 * @param operation - The Apollo operation being dispatched
 * @returns True when the operation must not share a batch
 */
export function isHeavyOperation(operation: Operation): boolean {
  if (!HEAVY_OPERATIONS.has(operation.operationName)) return false;
  const first: unknown = operation.variables?.first;
  return typeof first !== "number" || first >= HEAVY_MIN_PAGE_SIZE;
}

/**
 * Determines whether an operation is a GraphQL `query` (as opposed to a
 * mutation or subscription).
 *
 * Used as the {@link split} predicate for the terminal link: only queries are
 * eligible for batching, because coalescing concurrent reads into one request
 * is safe, while mutations must stay independent and subscriptions are
 * long-lived streams.
 * @param operation - The Apollo operation being dispatched
 * @returns True if the operation's main definition is a query
 */
export function isQueryOperation(operation: Operation): boolean {
  const definition = getMainDefinition(operation.query);
  return (
    definition.kind === "OperationDefinition" &&
    definition.operation === "query"
  );
}

/**
 * Decides whether an operation takes the batched transport.
 *
 * A batched POST resolves only when its SLOWEST member does, so a query the
 * app's routing depends on must be able to opt out with
 * `context: { batch: false }`. `AuthMe` does: on a cold load it was coalesced
 * behind nine warm-up queries and the whole batch stalled ~26s, leaving the
 * current user unresolved — long enough for flag-gated routes to give up and
 * bounce the user to Home. Unbatched it answers in well under a second
 * (SE-6233). A heavy page ({@link isHeavyOperation}) opts out permanently, for
 * the inverse reason: it is the slow member (SE-8579).
 * @param operation - The Apollo operation being dispatched
 * @returns True when the operation should be coalesced into a batch
 */
export function shouldBatchOperation(operation: Operation): boolean {
  if (operation.getContext().batch === false) return false;
  if (isHeavyOperation(operation)) return false;
  return isQueryOperation(operation);
}

/**
 * Decides whether an unbatched operation takes the heavy transport.
 *
 * `batch: false` wins: it is how the transport-timeout retry sends its second
 * attempt out alone on the plain link's single-op budget (SE-7338), and a
 * heavy page keeps that behaviour.
 * @param operation - The Apollo operation being dispatched
 * @returns True when the operation goes to the heavy `HttpLink`
 */
function shouldUseHeavyTransport(operation: Operation): boolean {
  return operation.getContext().batch !== false && isHeavyOperation(operation);
}

/**
 * Builds the batch key used by {@link BatchHttpLink} to decide which queries
 * may share a single HTTP request.
 *
 * Only operations that resolve to the SAME key are batched together. We key on
 * `authorization` and `x-pov-team-id` because those two headers determine the
 * server-side identity and POV the request executes under. Batching across
 * differing values would send one request carrying a single header set,
 * leaking one user's/team's POV onto another's queries. Both headers are read
 * from `operation.getContext().headers` — the same place `authLink` and
 * `createPovTeamHeaderLink` write them — so the key always reflects the headers
 * the request will actually be sent with.
 * @param operation - The Apollo operation being dispatched
 * @returns A string key; operations with equal keys are eligible to batch
 */
export function buildBatchKey(operation: Operation): string {
  const headers =
    (operation.getContext().headers as Record<string, string> | undefined) ??
    {};
  return [headers.authorization, headers[POV_TEAM_ID_HEADER]].join("|");
}

/**
 * Builds the terminal link: queries → batched transport, everything else →
 * plain transport.
 *
 * The batch (query) link and the plain (mutation/subscription) link can take
 * DIFFERENT fetch implementations. This matters for SE-5055's timeout fetch:
 * a batched POST's duration is gated by the SLOWEST of its N co-batched
 * queries, so the batch link intentionally gets a LONGER timeout than the
 * unbatched link — otherwise one slow co-batched query would abort the whole
 * batch and blank the page (SE-5038 regression). `batchFetch` defaults to
 * `fetch` for back-compat; when both are omitted, Apollo's default fetch is
 * used.
 *
 * Heavy pages (SE-8579) get a third transport: an unbatched `HttpLink` of
 * their own, so they can keep the batch link's longer budget. They rode that
 * 25s timer while they were batched, and leaving the batch is about not
 * holding light queries behind them, not about giving them less time. Only
 * the terminal link forks, so every link above it (auth, POV and build
 * headers, the read-timeout retry, error handling, Sentry) runs the same for
 * all three. A per-name `batchKey` was the alternative, but it would still put
 * copies of one heavy query into a single POST and hold them for the window.
 * @param uri - The GraphQL HTTP endpoint every transport POSTs to
 * @param fetch - Fetch for the unbatched link (mutations/subscriptions); also the default for the batch link
 * @param batchFetch - Fetch for the batched query link (typically a longer-timeout variant); falls back to `fetch`
 * @param heavyFetch - Fetch for heavy pages sent alone; falls back to `batchFetch`, then `fetch`
 * @returns An {@link ApolloLink} that routes by operation type and weight
 */
export function createTerminalLink(
  uri: string,
  fetch?: HttpOptions["fetch"],
  batchFetch?: HttpOptions["fetch"],
  heavyFetch?: HttpOptions["fetch"]
): ApolloLink {
  const httpLink = new HttpLink({ uri, fetch });
  const heavyHttpLink = new HttpLink({
    uri,
    fetch: heavyFetch ?? batchFetch ?? fetch,
  });
  const batchHttpLink = new BatchHttpLink({
    uri,
    fetch: batchFetch ?? fetch,
    batchMax: BATCH_MAX,
    batchInterval: BATCH_INTERVAL_MS,
    batchKey: buildBatchKey,
  });
  return split(
    shouldBatchOperation,
    batchHttpLink,
    split(shouldUseHeavyTransport, heavyHttpLink, httpLink)
  );
}

/**
 * The terminal link the app uses, with its SE-5055 timeout budgets: 15s for
 * the plain link, 25s for the batch link and for heavy pages sent alone.
 * Built here rather than inline in the provider so the budgets each path gets
 * are testable.
 * @param uri - The GraphQL HTTP endpoint
 * @returns The app's terminal {@link ApolloLink}
 */
export function createAppTerminalLink(uri: string): ApolloLink {
  return createTerminalLink(
    uri,
    createTimeoutFetch(REQUEST_TIMEOUT_MS),
    createTimeoutFetch(BATCH_REQUEST_TIMEOUT_MS),
    createTimeoutFetch(BATCH_REQUEST_TIMEOUT_MS)
  );
}
